import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut } from 'lucide-react';
import './chemicals-mobile.css';
import DashboardTab from './tabs/DashboardTab.jsx';
import ProductsTab from './tabs/ProductsTab.jsx';
import CustomersTab from './tabs/CustomersTab.jsx';
import VendorsTab from './tabs/VendorsTab.jsx';
import InventoryTab from './tabs/InventoryTab.jsx';
import FormulasTab from './tabs/FormulasTab.jsx';
import ProductionTab from './tabs/ProductionTab.jsx';
import PurchasesTab from './tabs/PurchasesTab.jsx';
import SalesTab from './tabs/SalesTab.jsx';
import LedgerTab from './tabs/LedgerTab.jsx';
import GatePassTab from './tabs/GatePassTab.jsx';
import ExpensesTab from './tabs/ExpensesTab.jsx';
import SettingsTab from './tabs/SettingsTab.jsx';
import ReportsTab from './tabs/ReportsTab.jsx';

const ChemicalsDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  const isBusinessman = user?.role === 'businessman';

  const allTabs = [
    { id: 'dashboard',  label: 'Dashboard',  icon: '📊', adminOnly: false },
    { id: 'products',   label: 'Products',   icon: '📦', adminOnly: false },
    { id: 'inventory',  label: 'Inventory',  icon: '🗃️', adminOnly: false },
    { id: 'production', label: 'Production', icon: '🏭', adminOnly: false },
    { id: 'purchases',  label: 'Purchases',  icon: '🛒', adminOnly: false },
    { id: 'sales',      label: 'Sales',      icon: '💼', adminOnly: false },
    { id: 'customers',  label: 'Customers',  icon: '👥', adminOnly: false },
    { id: 'vendors',    label: 'Vendors',    icon: '🏪', adminOnly: false },
    { id: 'ledger',     label: 'Ledger',     icon: '💰', adminOnly: false },
    { id: 'expenses',   label: 'Expenses',   icon: '💸', adminOnly: false },
    { id: 'reports',    label: 'Reports',    icon: '📈', adminOnly: false },
    { id: 'settings',   label: 'Settings',   icon: '⚙️', adminOnly: false },
  ];

  const visibleTabs = allTabs.filter((t) => !t.adminOnly || !isBusinessman);

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard':  return <DashboardTab />;
      case 'products':   return <ProductsTab />;
      case 'inventory':  return <InventoryTab />;
      case 'formulas':   return <FormulasTab />;
      case 'production': return <ProductionTab />;
      case 'purchases':  return <PurchasesTab />;
      case 'sales':      return <SalesTab />;
      case 'customers':  return <CustomersTab />;
      case 'vendors':    return <VendorsTab />;
      case 'ledger':     return <LedgerTab />;
      case 'gatepass':   return <GatePassTab />;
      case 'expenses':   return <ExpensesTab />;
      case 'reports':    return <ReportsTab />;
      case 'settings':   return <SettingsTab />;
      default:           return <DashboardTab />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" }}>

      {/* ─── Header ─────────────────────────────── */}
      <div className="chem-header" style={{
        background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
        color: 'white',
        padding: '24px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div className="chem-header-left" style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          {isBusinessman && (
            <button
              className="chem-header-back"
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '10px 20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.3)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.2)'; }}
            >
              ← Back
            </button>
          )}
          <h1 className="chem-header-title" style={{ fontSize: '28px', margin: 0, fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="chem-header-icon" style={{ fontSize: '32px' }}>⚗️</span>
            SwissChem
            <span className="chem-header-subtitle" style={{ fontSize: '13px', fontWeight: '500', opacity: 0.85, marginLeft: '8px' }}>
              Textile & dye chemicals
            </span>
          </h1>
        </div>

        <div className="chem-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="chem-header-user" style={{
            padding: '10px 16px',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            👤 <span>{user?.full_name || user?.username || 'User'}</span>
          </div>
          <button
            className="chem-header-logout"
            onClick={logout}
            style={{
              padding: '12px 24px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.3)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.2)'; }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* ─── Tab Bar ─────────────────────────────── */}
      <div className="chem-tabbar" style={{
        background: 'white',
        padding: '0 30px',
        borderBottom: '2px solid #f0f0f0',
        display: 'flex',
        gap: '4px',
        overflowX: 'auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            className="chem-tab"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '14px 18px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '700',
              color: activeTab === tab.id ? '#e74c3c' : '#7f8c8d',
              borderBottom: activeTab === tab.id ? '3px solid #e74c3c' : '3px solid transparent',
              marginBottom: '-2px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              transition: 'color 0.2s',
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ─── Tab Content ─────────────────────────── */}
      <div>
        {renderTab()}
      </div>

    </div>
  );
};

export default ChemicalsDashboard;